/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  APIResponseInterface,
  PaymentInitialProperties,
} from '../../interface/payment-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import cpoTransactionApi from '../../utils/api/cpo-transaction';

import { setNotificationMessage } from './NotificationManagementSlice';

// Define the type of data expected
interface FetchParameters {
  search?: string;
  page?: number;
  limit?: number;
}


/**
 * Async thunk to fetch all CDR transactions.
 * @param {FetchParameters} data - The query parameters for fetching transactions.
 * @returns {Promise<APIResponseInterface>} Resolves with API response interface.
 */
export const fetchAllCDRTransactions = createAsyncThunk<APIResponseInterface, FetchParameters>(
  'payment-management/fetchAllCDRTransactions',
  async (data: FetchParameters, { dispatch }) => {
    const apiCall = cpoTransactionApi.fetchCpoTransactions(removeEmptyKeys(data));

    const response = await apiCall
      .then((response1) => response1)
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );
    return {
      ...response.data,
      results: response.data.results.map((roleData: any) => ({
        ...roleData,
        date: ConvertUTCtoDate(roleData.createdAt),
        time: FormatUtcString(ConvertUTCtoHHMM(roleData.createdAt)),
        id: roleData._id,
      })),
    };
  },
);

/**
 * Legacy alias for fetchAllCDRTransactions.
 */
export const fetchAllPayments = fetchAllCDRTransactions;

/**
 * Async thunk to refresh all CDR transactions.
 * @param {FetchParameters} data - The query parameters for refreshing transactions.
 * @returns {Promise<APIResponseInterface>} Resolves with API response interface.
 */
export const refreshAllCDRTransactions = createAsyncThunk<APIResponseInterface, FetchParameters>(
  'payment-management/refreshAllCDRTransactions',
  async (data: FetchParameters) => {
    const apiCall = cpoTransactionApi.fetchCpoTransactions(removeEmptyKeys(data));

    const response = await apiCall;
    return {
      ...response.data,
      results: response.data.results.map((roleData: any) => ({
        ...roleData,
        date: ConvertUTCtoDate(roleData.createdAt),
        time: FormatUtcString(ConvertUTCtoHHMM(roleData.createdAt)),
        id: roleData._id,
      })),
    };
  },
);

/**
 * Legacy alias for refreshAllCDRTransactions.
 */
export const refreshAllPayments = refreshAllCDRTransactions;


/**
 * Async thunk to fetch payment method filters.
 * @returns {Promise<CommonFilterProperties[]>} Resolves with array of filter properties.
 */
export const fetchListPaymentMethodFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('payment-management/fetchListPaymentMethodFilters', async (_, { dispatch }) => {
  try {
    const response = await cpoTransactionApi.fetchCpoTransactionFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// Apply default filters
const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);


const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },

];

// Initial state
const initialState: PaymentInitialProperties = {
  payments: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const paymentSlice = createSlice({
  name: 'payment-management',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updatePaymentManagementFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state.
     */
    disablePaymentManagementApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Function for setting sortBy and sortField
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllCDRTransactions.pending, (state: PaymentInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllCDRTransactions.fulfilled,
        (state: PaymentInitialProperties, action: { payload: APIResponseInterface }) => {
          state.loading = false;
          state.payments = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllCDRTransactions.rejected, (state: PaymentInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder.addCase(
      refreshAllCDRTransactions.fulfilled,
      (state: PaymentInitialProperties, action: { payload: APIResponseInterface }) => {
        state.payments = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );
    builder
      .addCase(fetchListPaymentMethodFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListPaymentMethodFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload].filter(
            (object, index, self) => index === self.findIndex((t) => t.key === object.key),
          );
        },
      )
      .addCase(fetchListPaymentMethodFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updatePaymentManagementFilters, disablePaymentManagementApplied, setSortData } =
  paymentSlice.actions;
export default paymentSlice.reducer;

